import React, { type ReactElement, type Ref, useRef, useState } from "react"
import useBreakpoint from "../../hooks/useBreakpoint"
import Popover, { type PopoverPropsType } from "."

type RefableElement = ReactElement & {
  ref?: Ref<HTMLElement>
}

export type PopoverWrapperPropsType = Omit<
  PopoverPropsType,
  "targetRef" | "children" | "isOpen"
> & {
  content: React.ReactNode
  disabled?: boolean
  isOpenByDefault?: boolean
  children: RefableElement
}

const mergeRefs = (
  node: HTMLElement | null,
  originalRef: Ref<HTMLElement> | undefined,
  buttonRef: React.MutableRefObject<HTMLElement | null>,
  extraRef?: (node: HTMLElement | null) => void,
) => {
  buttonRef.current = node
  extraRef?.(node)

  if (typeof originalRef === "function") {
    originalRef(node)
  } else if (originalRef && typeof originalRef === "object") {
    ;(originalRef as React.MutableRefObject<HTMLElement | null>).current = node
  }
}

export function PopoverWrapper({
  content,
  children,
  trigger,
  onOpenChange,
  isOpenByDefault = false,
  disabled = false,
  responsive = true,
  ...popoverProps
}: PopoverWrapperPropsType) {
  const buttonRef = useRef<HTMLElement | null>(null)
  const [isOpen, setIsOpen] = useState(isOpenByDefault)
  const { breakpoint } = useBreakpoint()
  const isMobile = responsive && (breakpoint === "sm" || breakpoint === "xs")

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    onOpenChange?.(open)
  }

  if (disabled) return children

  if (isMobile) {
    const childProps = children.props as {
      onClick?: (event: React.MouseEvent<HTMLElement>) => void
    }

    return (
      <>
        {React.cloneElement(children, {
          onClick: (event: React.MouseEvent<HTMLElement>) => {
            childProps.onClick?.(event)
            handleOpenChange(true)
          },
          ref: (node: HTMLElement | null) =>
            mergeRefs(node, children.ref, buttonRef),
        })}
        <Popover
          {...popoverProps}
          responsive
          isOpen={isOpen}
          onOpenChange={handleOpenChange}
          targetRef={buttonRef}
        >
          {content}
        </Popover>
      </>
    )
  }

  return (
    <Popover
      {...popoverProps}
      responsive={false}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      targetRef={buttonRef}
      trigger={trigger}
      interactive={popoverProps.interactive ?? trigger === "mouseenter"}
    >
      {({ refs, getReferenceProps, renderFloating }) => {
        const referenceProps = getReferenceProps(children.props)

        return (
          <>
            {React.cloneElement(children, {
              ...referenceProps,
              ref: (node: HTMLElement | null) =>
                mergeRefs(node, children.ref, buttonRef, refs.setReference),
            })}
            {renderFloating(content)}
          </>
        )
      }}
    </Popover>
  )
}
